import type { MaybeRefOrGetter, Ref } from 'vue';
import type { CompositePrimaryKey } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { stringifyPrimaryKey } from '#layers/data-model/public';

export type SelectionMode = 'single' | 'multiple';

interface UseSelectionOptions {
	selection: Ref<CompositePrimaryKey[]>;
	selectionMode?: MaybeRefOrGetter<SelectionMode | undefined>;
}

export function useSelection(options: UseSelectionOptions) {
	const currentMode = computed(() => toValue(options.selectionMode) ?? 'multiple');

	function clear() {
		options.selection.value = [];
	}

	function add(...keys: CompositePrimaryKey[]) {
		if (keys.length === 0) return;

		if (currentMode.value === 'single' && keys[0]) {
			options.selection.value = [keys[0]];
		}
		else {
			const currentSet = new Set(options.selection.value.map((key) => JSON.stringify(key)));

			for (const key of keys) {
				currentSet.add(stringifyPrimaryKey(key));
			}

			options.selection.value = [...currentSet].map((key) => JSON.parse(key) as CompositePrimaryKey);
		}
	}

	function remove(...keys: CompositePrimaryKey[]) {
		if (keys.length === 0) return;

		const keysToRemove = new Set(keys.map((key) => stringifyPrimaryKey(key)));

		options.selection.value = options.selection.value.filter((key) => !keysToRemove.has(stringifyPrimaryKey(key)));
	}

	function toggleSelection(key: CompositePrimaryKey) {
		isSelected(key) ? remove(key) : add(key);
	}

	function isSelected(key: CompositePrimaryKey) {
		return options.selection.value.map((key) => stringifyPrimaryKey(key)).includes(stringifyPrimaryKey(key));
	}

	const isSelectionEmpty = computed(() => options.selection.value.length === 0);

	return {
		add,
		remove,
		isSelected,
		toggleSelection,
		isSelectionEmpty,
		clear,
	};
}
